import { Component, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AnimationState } from '../models/animation-state.enum';

const STATUS_QUERYING_DATA = 'querying_data';
const STATUS_QUERY_RESULT = 'query_result';

const LOADING_PREFIX = 'ai_chat.loading';

@Component({
	selector: 'ai-chat-loader',
	templateUrl: 'ai-chat-loader.component.html',
	styleUrl: 'ai-chat-loader.component.scss',
})
export class AIChatLoaderComponent {
	private translate = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	statusMessage = input<string | null>(null);
	statusParams = input<Record<string, unknown> | null>(null);

	readonly displayedText = signal('');
	readonly animationState = signal<AnimationState>(AnimationState.VISIBLE);

	readonly AnimationState = AnimationState;

	private transitionTimer: any = null;
	/** Last rendered status as `key + params`, so a recurring key with new params re-renders. */
	private lastFingerprint: string | null = null;

	private readonly transitionDuration = 300;

	constructor() {
		this.displayedText.set(this.translate.instant(`${LOADING_PREFIX}.thinking`));
		this.destroyRef.onDestroy(() => clearTimeout(this.transitionTimer));

		effect(() => {
			const key = this.statusMessage();
			const params = this.statusParams();
			if (!key) return;
			const fingerprint = key + (params ? JSON.stringify(params) : '');
			if (fingerprint !== this.lastFingerprint) {
				this.transitionTo(this.buildStatusText(key, params), fingerprint);
			}
		});
	}

	/** Resolve the displayed text for a status, templating `querying_data` from its params. */
	private buildStatusText(key: string, params: Record<string, unknown> | null): string {
		if (key === STATUS_QUERYING_DATA && params) {
			const count = params['vehicleCount'];
			const dimension = params['dimension'];
			// Localize measure keys via the existing chart_value_properties labels (analytics
			// i18n, where querying_data fires); fall back to a humanized key otherwise.
			const measureKeys = Array.isArray(params['measures']) ? (params['measures'] as string[]) : [];
			const measures = measureKeys
				.map((m) => this.translateOr(`chart_value_properties.${m}.value`, m.replace(/_/g, ' ')))
				.join(', ');
			if (typeof dimension === 'string') {
				return this.translate.instant(`${LOADING_PREFIX}.${STATUS_QUERYING_DATA}_grouped`, {
					measures,
					dimension: this.translateOr(`ai_chat.dimensions.${dimension}`, dimension),
					count,
				});
			}
			const names = Array.isArray(params['vehicleNames'])
				? (params['vehicleNames'] as string[])
				: [];
			if (names.length) {
				const total = typeof count === 'number' ? count : names.length;
				const others = total - names.length;
				const namesStr = names.join(', ');
				return others > 0
					? this.translate.instant(`${LOADING_PREFIX}.${STATUS_QUERYING_DATA}_named_more`, {
							measures,
							names: namesStr,
							others,
						})
					: this.translate.instant(`${LOADING_PREFIX}.${STATUS_QUERYING_DATA}_named`, {
							measures,
							names: namesStr,
						});
			}
			return this.translate.instant(`${LOADING_PREFIX}.${STATUS_QUERYING_DATA}`, {
				measures,
				count,
			});
		}
		if (key === STATUS_QUERY_RESULT && params) {
			// Outcome beat for grouped queries: "Found 69 workflows…" (plural dimension noun).
			const dimension = params['dimension'];
			return this.translate.instant(`${LOADING_PREFIX}.${STATUS_QUERY_RESULT}`, {
				count: params['count'],
				dimension:
					typeof dimension === 'string'
						? this.translateOr(`ai_chat.dimensions_plural.${dimension}`, dimension)
						: '',
			});
		}
		return this.translateStatus(key);
	}

	/** translate.instant, falling back to a literal when the key is missing. */
	private translateOr(key: string, fallback: string): string {
		const translated = this.translate.instant(key);
		return translated !== key ? translated : fallback;
	}

	private translateStatus(key: string): string {
		const translationKey = `${LOADING_PREFIX}.${key}`;
		const translated = this.translate.instant(translationKey);
		return translated !== translationKey ? translated : key;
	}

	private transitionTo(text: string, fingerprint: string) {
		clearTimeout(this.transitionTimer);

		if (!this.lastFingerprint) {
			this.lastFingerprint = fingerprint;
			this.displayedText.set(text);
			this.animationState.set(AnimationState.VISIBLE);
			return;
		}

		this.animationState.set(AnimationState.EXITING);

		this.transitionTimer = setTimeout(() => {
			this.displayedText.set(text);
			this.lastFingerprint = fingerprint;
			this.animationState.set(AnimationState.ENTERING);

			// Reset to visible after animation completes
			this.transitionTimer = setTimeout(() => {
				this.animationState.set(AnimationState.VISIBLE);
			}, this.transitionDuration);
		}, this.transitionDuration);
	}
}
